import React from 'react';
import PropTypes from 'prop-types';
import Chip from '@mui/material/Chip';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const DISPOSITION_CONFIG = {
  ACCEPTED: {
    color: 'success',
    label: 'Accepted',
    Icon: CheckCircleOutlineIcon,
  },
  REVIEW_NEEDED: {
    color: 'warning',
    label: 'Review Needed',
    Icon: WarningAmberIcon,
  },
  REJECTED: {
    color: 'error',
    label: 'Rejected',
    Icon: CancelOutlinedIcon,
  },
};

const DEFAULT_CONFIG = {
  color: 'default',
  label: 'Unknown',
  Icon: HelpOutlineIcon,
};

/**
 * DispositionChip renders a color-coded chip with an icon for the scan disposition.
 * Supports ACCEPTED, REVIEW_NEEDED, and REJECTED states.
 *
 * @param {Object} props
 * @param {string} props.disposition - Disposition code (e.g. "ACCEPTED")
 * @param {string} [props.size] - MUI Chip size
 * @param {Object} [props.sx] - Additional MUI sx styles
 */
const DispositionChip = ({ disposition, size = 'small', sx = {}, ...rest }) => {
  const config = DISPOSITION_CONFIG[(disposition || '').toUpperCase()] || {
    ...DEFAULT_CONFIG,
    label: disposition || DEFAULT_CONFIG.label,
  };

  const { color, label, Icon } = config;

  return (
    <Chip
      icon={<Icon fontSize="small" />}
      label={label}
      color={color}
      size={size}
      variant="filled"
      sx={{ fontWeight: 600, ...sx }}
      {...rest}
    />
  );
};

DispositionChip.propTypes = {
  disposition: PropTypes.string.isRequired,
  size: PropTypes.oneOf(['small', 'medium']),
  sx: PropTypes.object,
};

export default DispositionChip;